/**
 * Settings > Billing (M27 PR 3) — /settings/billing.
 */

import { useQuery } from "@tanstack/react-query";
import {
  Calculator,
  ChevronRight,
  CreditCard,
  Database,
  FileText,
  Mail,
  MessageSquare,
  Receipt,
  Sparkles,
  Users,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ErrorPanel, Loading } from "../../components/Loading";
import { PageHeader } from "../../components/ui";
import { api } from "../../lib/api";
import type { BillingUsage } from "../../lib/api";

const _SALES_EMAIL = "sales@solvait.ai";

const _PRICE_BAND_LOW = 8;
const _PRICE_BAND_HIGH = 15;

export function BillingPage() {
  const { t } = useTranslation();
  const [showEstimate, setShowEstimate] = useState(false);
  const q = useQuery({
    queryKey: ["billing-usage"],
    queryFn: () => api.getBillingUsage(),
    refetchInterval: 60_000,
  });

  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorPanel message={(q.error as Error).message} />;
  const usage: BillingUsage = q.data!;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t("app.settings_billing_title")}
        subtitle={t("app.bill_subtitle")}
        breadcrumbs={[
          { label: t("app.settings_breadcrumb"), href: "/settings" },
          { label: t("app.settings_billing_title") },
        ]}
      />

      <section className="rounded-xl border border-border-subtle bg-surface-default p-5">
        <header className="mb-3 flex items-center gap-2">
          <CreditCard size={16} className="text-brand-blue" aria-hidden />
          <h2 className="text-sm font-semibold text-text-primary">
            {t("app.bill_usage_today")}
          </h2>
          <span className="ms-auto text-xs text-text-tertiary">
            {t("app.bill_updates_every_minute", { month: monthLabel(usage.period_start) })}
          </span>
        </header>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
          <UsageTile
            icon={Users}
            label={t("app.bill_active_employees")}
            value={usage.active_employees.toLocaleString()}
            hint={t("app.bill_active_employees_hint")}
          />
          <UsageTile
            icon={FileText}
            label={t("app.bill_active_les")}
            value={usage.active_legal_entities.toLocaleString()}
            hint={t("app.bill_active_les_hint")}
          />
          <UsageTile
            icon={Sparkles}
            label={t("app.bill_ai_calls_month")}
            value={usage.ai_calls_this_month.toLocaleString()}
            hint={t("app.bill_ai_cache_hit_hint", { pct: (usage.ai_cache_hit_rate * 100).toFixed(1) })}
          />
          <UsageTile
            icon={Sparkles}
            label={t("app.bill_ai_cost_month")}
            value={`$${Number(usage.ai_cost_usd_this_month).toFixed(2)}`}
            hint={t("app.bill_ai_cost_hint")}
          />
          <UsageTile
            icon={MessageSquare}
            label={t("app.bill_concierge_msgs")}
            value={usage.concierge_messages_this_month.toLocaleString()}
            hint={t("app.bill_concierge_hint")}
          />
          <UsageTile
            icon={Database}
            label={t("app.bill_storage")}
            value={`${usage.storage_used_mb.toFixed(2)} MB`}
            hint={t("app.bill_storage_hint")}
          />
        </div>
      </section>

      <section className="rounded-xl border border-border-subtle bg-surface-default p-5">
        <header className="mb-2 flex items-center gap-2">
          <Calculator size={16} className="text-brand-blue" aria-hidden />
          <h2 className="text-sm font-semibold text-text-primary">
            {t("app.bill_estimate_title")}
          </h2>
          <button
            type="button"
            onClick={() => setShowEstimate((v) => !v)}
            className="ms-auto rounded border border-border-subtle px-2 py-1 text-xs text-text-secondary hover:bg-surface-sunken"
          >
            {showEstimate ? t("app.bill_estimate_hide") : t("app.bill_estimate_show")}
          </button>
        </header>
        {!showEstimate ? (
          <p className="text-xs text-text-tertiary">
            {t("app.bill_estimate_intro")}
          </p>
        ) : (
          <EstimateBlock employees={usage.active_employees} />
        )}
      </section>

      <section className="rounded-xl border border-border-subtle bg-surface-default p-5">
        <header className="mb-3 flex items-center gap-2">
          <Receipt size={16} className="text-brand-blue" aria-hidden />
          <h2 className="text-sm font-semibold text-text-primary">
            {t("app.bill_will_include")}
          </h2>
        </header>
        <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {[
            t("app.bill_will_include_1"),
            t("app.bill_will_include_2"),
            t("app.bill_will_include_3"),
            t("app.bill_will_include_4"),
            t("app.bill_will_include_5"),
            t("app.bill_will_include_6"),
          ].map((item) => (
            <li
              key={item}
              className="flex items-start gap-2 rounded border border-border-subtle bg-surface-sunken px-3 py-2 text-xs text-text-secondary"
            >
              <ChevronRight
                size={12}
                aria-hidden
                className="mt-0.5 text-text-tertiary rtl:rotate-180"
              />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="rounded-xl border border-ai-subtle bg-ai-bg p-5">
        <header className="flex items-start gap-3">
          <Mail size={18} className="mt-0.5 text-ai" aria-hidden />
          <div>
            <h2 className="text-sm font-semibold text-text-primary">
              {t("app.bill_talk_pricing")}
            </h2>
            <p className="mt-1 text-xs text-text-secondary">
              {t("app.bill_talk_pricing_body")}
            </p>
          </div>
          <a
            href={`mailto:${_SALES_EMAIL}?subject=Solvait pricing inquiry`}
            className="ms-auto inline-flex shrink-0 items-center gap-1.5 rounded bg-brand px-3 py-1.5 text-sm font-medium text-brand-fg hover:opacity-90"
          >
            <Mail size={14} aria-hidden /> {t("app.bill_contact_sales")}
          </a>
        </header>
      </section>
    </div>
  );
}

function UsageTile({
  icon: Icon,
  label,
  value,
  hint,
}: {
  icon: typeof Users;
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="rounded-lg border border-border-subtle bg-surface-default p-3">
      <header className="flex items-center gap-1.5">
        <Icon size={12} className="text-text-tertiary" aria-hidden />
        <p className="text-xs text-text-secondary">{label}</p>
      </header>
      <p className="mt-1 font-mono text-lg font-semibold tabular-nums text-text-primary">
        {value}
      </p>
      {hint && (
        <p className="mt-0.5 text-xs text-text-tertiary">{hint}</p>
      )}
    </div>
  );
}

function EstimateBlock({ employees }: { employees: number }) {
  const { t } = useTranslation();
  const low = (employees * _PRICE_BAND_LOW).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  const high = (employees * _PRICE_BAND_HIGH).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
      <p className="text-sm text-amber-900">
        {t("app.bill_estimate_band", {
          low: _PRICE_BAND_LOW,
          high: _PRICE_BAND_HIGH,
          employees: employees.toLocaleString(),
          totalLow: low,
          totalHigh: high,
        })}
      </p>
      <p className="mt-2 text-xs text-amber-800">
        {t("app.bill_estimate_disclaimer")}
      </p>
    </div>
  );
}

function monthLabel(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: "long",
    year: "numeric",
  });
}
